import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetArrow, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
            padding: '0 15px',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            '& .h2': {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            '& .h3': {
                paddingTop: '10px',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        span: {
            fontSize: '1rem',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum OptionsEnum {
    LAST_WEEK,
    LAST_14_DAYS,
    LAST_30_DAYS,
    LAST_12_MOTNHS,
}

interface FeesCollectedByPeriodPartChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    options: OptionsEnum;
    months: string[];
    counts: number[];
    currentTotal: number;
    previousTotal: number;
    periodString: string;
    symbol: string;
}

export const FeesCollectedByPeriodPartChart: React.FC<FeesCollectedByPeriodPartChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const feesCollectedByPeriod = useQuery<FeesCollectedByPeriodQueryData>(FeesCollectedByPeriodQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        options: OptionsEnum.LAST_WEEK,
        months: [],
        counts: [],
        currentTotal: 0,
        previousTotal: 0,
        periodString: 'Week',
        symbol: '$'
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        setState((prevState) => {
            let months: string[] = [];
            let counts: number[] = [];
            let periodString: string = 'Week';
            let currentTotal: number = 0;
            let previousTotal: number = 0;

            if (!feesCollectedByPeriod.error && feesCollectedByPeriod.data) {
                let lastWeek = feesCollectedByPeriod.data.statistics.fees.feesCollectedByPeriod.lastWeek;
                let last14Days = feesCollectedByPeriod.data.statistics.fees.feesCollectedByPeriod.last14Days;
                let last30Days = feesCollectedByPeriod.data.statistics.fees.feesCollectedByPeriod.last30Days;
                let last12Months = feesCollectedByPeriod.data.statistics.fees.feesCollectedByPeriod.last12Months;

                if (state.options === OptionsEnum.LAST_WEEK) {
                    months = lastWeek.feesCollectedGrouping.map((s) => s.group);
                    counts = lastWeek.feesCollectedGrouping.map((s) => s.value);
                    currentTotal = lastWeek.summary.lastPeriodTotal;
                    previousTotal = lastWeek.summary.previousPeriodTotal;
                    periodString = 'Week';
                } else if (state.options === OptionsEnum.LAST_14_DAYS) {
                    months = last14Days.feesCollectedGrouping.map((s) => s.group);
                    counts = last14Days.feesCollectedGrouping.map((s) => s.value);
                    currentTotal = last14Days.summary.lastPeriodTotal;
                    previousTotal = last14Days.summary.previousPeriodTotal;
                    periodString = '14 Days';
                } else if (state.options === OptionsEnum.LAST_30_DAYS) {
                    months = last30Days.feesCollectedGrouping.map((s) => s.group);
                    counts = last30Days.feesCollectedGrouping.map((s) => s.value);
                    currentTotal = last30Days.summary.lastPeriodTotal;
                    previousTotal = last30Days.summary.previousPeriodTotal;
                    periodString = '30 Days';
                } else {
                    months = last12Months.feesCollectedGrouping.map((s) => s.group);
                    counts = last12Months.feesCollectedGrouping.map((s) => s.value);
                    currentTotal = last12Months.summary.lastPeriodTotal;
                    previousTotal = last12Months.summary.previousPeriodTotal;
                    periodString = '12 Months';
                }
            }

            return {
                ...prevState,
                counts,
                months,
                periodString,
                currentTotal,
                previousTotal,
            };
        });
    // tslint:disable-next-line: align
    }, [state.options, feesCollectedByPeriod.loading, feesCollectedByPeriod.error, feesCollectedByPeriod.data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFeesCollectedByPeriodPartChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isPrevious: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (state.options === OptionsEnum.LAST_WEEK) {
                searchCriterias.push({ property: 'PeriodInDays', operator: 'eq', comparison: '7', period: isPrevious ? 'prev' : 'curr' });
            } else if (state.options === OptionsEnum.LAST_30_DAYS) {
                searchCriterias.push({ property: 'PeriodInDays', operator: 'eq', comparison: '30', period: isPrevious ? 'prev' : 'curr' });
            } else if (state.options === OptionsEnum.LAST_12_MOTNHS) {
                searchCriterias.push({ property: 'PeriodInDays', operator: 'eq', comparison: '12Months', period: isPrevious ? 'prev' : 'curr' });
            }

            let defaultView = 'Fees Collected MTD/YTD';

            if (state.options === OptionsEnum.LAST_WEEK) {
                defaultView = 'Fees Collected WTD';
            }

            props.getSelectedItem(searchCriterias, false, 'Fees Collected', DrillDownType.staffBillingSummary, defaultView);
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };
    
    if (currencySymbolQuery.loading || feesCollectedByPeriod.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.counts) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<MonetizationOnOutlinedIcon />} title={<>Fees Collected in {state.periodString === 'Week' ? 'This' : 'Last'} {state.periodString}</>} />
                <div className={`${classes.totalDiv} h2`} onClick={() => handleTotalClick(false)}>
                    <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                    {GetArrow(state.currentTotal, state.previousTotal)}
                </div>
                <div className={`${classes.totalDiv} h1`} onClick={() => handleTotalClick(true)}>
                    {`${state.symbol}${formatNumber(state.previousTotal)} in the previous ${state.periodString}`}
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            // const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            // const item = state.months[_index];

                            // const searchCriterias = new Array<SearchCriteria>();

                            // if (state.options === OptionsEnum.LAST_WEEK ||
                            //     state.options === OptionsEnum.LAST_14_DAYS ||
                            //     state.options === OptionsEnum.LAST_30_DAYS) {
                            //         searchCriterias.push(
                            //             { property: 'DateShortString', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May 05
                            //         );
                            // } else {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May
                            //     );
                            // }

                            // let defaultView = 'Fees Collected MTD/YTD';

                            // if (state.options === OptionsEnum.LAST_WEEK ) {
                            //     defaultView = 'Fees Collected WTD';
                            // }

                            // props.getSelectedItem(
                            //     searchCriterias,
                            //     false,
                            //     'Fees Collected - ' + item,
                            //     DrillDownType.staffBillingSummary,
                            //     defaultView
                            // );
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Fees Collected',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                                type: 'bar',
                            },
                        // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(state.counts),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.counts),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.counts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={OptionsEnum.LAST_WEEK} control={<Radio color="primary" />} label="This Week" />

                    <FormControlLabel value={OptionsEnum.LAST_14_DAYS} control={<Radio color="primary" />} label="Last 14 days" />
                    <FormControlLabel value={OptionsEnum.LAST_30_DAYS} control={<Radio color="primary" />} label="Last 30 days" />
                    <FormControlLabel value={OptionsEnum.LAST_12_MOTNHS} control={<Radio color="primary" />} label="Last 12 months" />
                </RadioGroup>
            </FormControl>
            <ChartNotes notes={['Information collated based on Timekeeper']} actionHint="Drill down to timekeeper details" />
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface FeesBilledByPeriodDataQueryParams {
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
}

export interface FeesCollectedByPeriodQueryData {
    statistics: Statistics;
}
export interface Statistics {
    fees: Fees;
}

export interface Fees {
    feesCollectedByPeriod: FeesCollectedByPeriod;
}

export interface FeesCollectedByPeriod {
    lastWeek: Last;
    last14Days: Last;
    last30Days: Last;
    last12Months: Last;
}

export interface Last {
    summary: Summary;
    feesCollectedGrouping: FeesCollectedGrouping[];
}

export interface FeesCollectedGrouping {
    group: string;
    value: number;
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
    variance: number;
    percentage: number;
    budget: number;
}

const FeesCollectedByPeriodQuery = gql`
    query FeesCollectedByPeriod($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            fees {
                feesCollectedByPeriod(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    lastWeek {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                    }
                    last14Days {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                    }
                    last30Days {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                    }
                    last12Months {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                    }
                }
            }
        }
    }
`;
